/**
 * Membership hook + preview-key plumbing.
 *
 * Sources of truth (in order):
 *   1. ?preview=<key>  — shareable URL preview link (founder / influencer /
 *      tester). Backend honours this when the key matches PREVIEW_KEY env;
 *      we mirror that here so the client UI hides locks even before the next
 *      paint cycle. The key itself is held only in the URL — never persisted.
 *   2. /auth/me        — authenticated member or admin (role==='admin',
 *      is_member===true, or shopify_tags includes 'active_member').
 *
 * Notes
 *   - This is a client-side convenience layer. The backend is the only
 *     authority — if the URL is wrong, the gated payload is what arrives.
 *   - To preview locked content via URL: append ?preview=<key> (alias
 *     ?member=<key>) on any page. Sticky for the session via sessionStorage
 *     so deep-link navigation keeps the preview state.
 */

import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";

const PREVIEW_STORAGE_KEY = "ctx_preview_key";

function readPreviewFromUrl() {
    if (typeof window === "undefined") return null;
    try {
        const params = new URLSearchParams(window.location.search);
        return params.get("preview") || params.get("member") || null;
    } catch {
        return null;
    }
}

function readStoredPreview() {
    if (typeof window === "undefined") return null;
    try {
        return window.sessionStorage.getItem(PREVIEW_STORAGE_KEY);
    } catch {
        return null;
    }
}

function storePreview(key) {
    if (typeof window === "undefined") return;
    try {
        if (key) window.sessionStorage.setItem(PREVIEW_STORAGE_KEY, key);
        else window.sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

export function getPreviewKey() {
    return readPreviewFromUrl() || readStoredPreview();
}

export function useMembership() {
    const { user, checking } = useAuth();
    const [previewKey, setPreviewKey] = useState(() => getPreviewKey());

    // Persist preview key on first arrival and listen for URL changes.
    useEffect(() => {
        const fromUrl = readPreviewFromUrl();
        if (fromUrl) {
            // Always reflect the latest URL value — including invalid keys —
            // so the client UI stays consistent with whatever the server will
            // accept on its next request.
            storePreview(fromUrl);
            setPreviewKey(fromUrl);
        }
        const onPop = () => {
            const next = readPreviewFromUrl() || readStoredPreview();
            setPreviewKey(next);
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    const isAuthMember = useMemo(() => {
        if (!user) return false;
        if (user.role === "admin") return true;
        if (user.is_member === true) return true;
        const tags = user.shopify_tags || [];
        return tags.some((t) => ["context-project-member", "active_member"].includes(t));
    }, [user]);

    const isPreview = Boolean(previewKey);
    const isMember = isAuthMember || isPreview;

    return {
        isMember,
        isAuthMember,
        isPreview,
        previewKey,
        checking,
        clearPreview: () => {
            storePreview(null);
            setPreviewKey(null);
        },
    };
}
